import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getReminders,
  getReminder,
  createReminder,
  updateReminder,
  deleteReminder,
  snoozeReminder,
  dismissReminder,
} from '../services/remindersApi';
import type {
  ReminderFilters,
  CreateReminderRequest,
  UpdateReminderRequest,
} from '../types/reminder';

// Query keys
export const reminderKeys = {
  all: ['reminders'] as const,
  lists: () => [...reminderKeys.all, 'list'] as const,
  list: (filters?: ReminderFilters) => [...reminderKeys.lists(), filters] as const,
  details: () => [...reminderKeys.all, 'detail'] as const,
  detail: (id: string) => [...reminderKeys.details(), id] as const,
};

/**
 * Hook to fetch reminders
 */
export const useReminders = (filters?: ReminderFilters) => {
  return useQuery({
    queryKey: reminderKeys.list(filters),
    queryFn: () => getReminders(filters),
  });
};

/**
 * Hook to fetch a single reminder
 */
export const useReminder = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: reminderKeys.detail(id),
    queryFn: () => getReminder(id),
    enabled: options?.enabled ?? !!id,
  });
};

/**
 * Hook for reminder mutations
 */
export const useReminderMutations = () => {
  const queryClient = useQueryClient();

  const invalidateReminders = () => {
    queryClient.invalidateQueries({ queryKey: reminderKeys.all });
  };

  const createReminderMutation = useMutation({
    mutationFn: (data: CreateReminderRequest) => createReminder(data),
    onSuccess: () => invalidateReminders(),
  });

  const updateReminderMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReminderRequest }) =>
      updateReminder(id, data),
    onSuccess: () => invalidateReminders(),
  });

  const deleteReminderMutation = useMutation({
    mutationFn: (id: string) => deleteReminder(id),
    onSuccess: () => invalidateReminders(),
  });

  const snoozeReminderMutation = useMutation({
    mutationFn: ({ id, minutes }: { id: string; minutes: number }) =>
      snoozeReminder(id, minutes),
    onSuccess: () => invalidateReminders(),
  });

  const dismissReminderMutation = useMutation({
    mutationFn: (id: string) => dismissReminder(id),
    onSuccess: () => invalidateReminders(),
  });

  return {
    createReminder: createReminderMutation,
    updateReminder: updateReminderMutation,
    deleteReminder: deleteReminderMutation,
    snoozeReminder: snoozeReminderMutation,
    dismissReminder: dismissReminderMutation,
    isLoading:
      createReminderMutation.isPending ||
      updateReminderMutation.isPending ||
      deleteReminderMutation.isPending ||
      snoozeReminderMutation.isPending ||
      dismissReminderMutation.isPending,
  };
};

export default useReminders;
